import React, { useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Grid,
  Divider,
  Tabs,
  Tab,
  Paper,
  useMediaQuery,
} from "@mui/material";
import * as MuiIcons from "@mui/icons-material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { InstructionItem } from "./functions";
import ContactForm from "../ContactForm";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useTheme } from "@mui/material/styles";
import { getStyles } from "../index";
import {
  lessonItems,
  quizItems,
  flashcardItems,
  textLabbetItems,
  gradingItems,
  powerpointItems,
  assistentItems,
  visionItems,
  imageItems,
  VideoPlayer,
  lessonPlanItems,
  hpItems,
  podcastItems,
} from "./HelpDialogItems";

import {
  podcastItemsStudent,
  hpItemsStudent,
  quizItemsStudent,
  flashcardItemsStudent,
  assistentItemsStudent,
  gradingItemsStudent,
  imageItemsStudent,
} from "./HelpDialogItemsStudents";

const HelpContent = ({
  quiz = false,
  flashcards = false,
  textLabbet = false,
  grading = false,
  powerpoint = false,
  assistent = false,
  overview = false,
  vision = false,
  lessonPlan = false,
  boxShadow = "rgba(0, 0, 0, 0.35) 0px 5px 15px",
  backgroundGradient = "linear-gradient(105deg, rgba(139, 92, 246, 1) 0%, rgba(147, 51, 234, 0.95) 50%, rgba(139, 92, 246, 0.98) 100%)",
  clipPathBackground = "linear-gradient(45deg, rgba(255,255,255,0.15) 0%, transparent 75%)",
  insideApp = false,
  podcast = false,
  user,
}) => {
  const theme = useTheme();
  const styles = getStyles(theme);
  const [tabValue, setTabValue] = useState(0);
  const [selectedContentType, setSelectedContentType] = useState(0);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const isTeacher = user?.accountType !== "student";

  // Definition av sektioner för navgiering
  const sections = [
    ...(isTeacher
      ? [{ id: 0, name: "Lektioner", icon: "School", items: lessonItems }]
      : []),

    {
      id: 1,
      name: "Quiz",
      icon: "Psychology",
      items: isTeacher ? quizItems : quizItemsStudent,
    },

    {
      id: 2,
      name: "Podcast",
      icon: "Mic",
      items: isTeacher ? podcastItems : podcastItemsStudent,
    },

    {
      id: 3,
      name: "Bedömning",
      icon: "Grading",
      items: isTeacher ? gradingItems : gradingItemsStudent,
    },

    {
      id: 4,
      name: "Flashcards",
      icon: "ViewAgenda",
      items: isTeacher ? flashcardItems : flashcardItemsStudent,
    },
    {
      id: 5,
      name: isTeacher ? "Prov" : "Högskoleprov",
      icon: "HistoryEdu",
      items: isTeacher ? hpItems : hpItemsStudent,
    },
    {
      id: 6,
      name: "Assistenter",
      icon: "SmartToy",
      items: isTeacher ? assistentItems : assistentItemsStudent,
    },
    ...(isTeacher
      ? [
          {
            id: 7,
            name: "Lektionsplan",
            icon: "CalendarMonth",
            items: lessonPlanItems,
          },
        ]
      : []),
    {
      id: 8,
      name: "Bilder",
      icon: "AddPhotoAlternate",
      items: isTeacher ? imageItems : imageItemsStudent,
    },
    // { id: 8, name: "Textlabbet", icon: "Edit", items: textLabbetItems },
    // ...(isTeacher
    //   ? [
    //       {
    //         id: 9,
    //         name: "PowerPoint",
    //         icon: "Slideshow",
    //         items: powerpointItems,
    //       },
    //     ]
    //   : []),
  ];

  // Filtrera sektioner baserat på props
  const getVisibleSections = () => {
    if (!overview) {
      if (quiz) return sections.filter((s) => s.name === "Quiz");
      if (flashcards) return sections.filter((s) => s.name === "Flashcards");
      if (textLabbet) return sections.filter((s) => s.name === "Textlabbet");
      if (powerpoint) return sections.filter((s) => s.name === "PowerPoint");
      if (grading) return sections.filter((s) => s.name === "Bedömning");
      if (assistent) return sections.filter((s) => s.name === "Assistenter");
      if (vision) return sections.filter((s) => s.name === "Bilder");
      if (lessonPlan) return sections.filter((s) => s.name === "Lektionsplan");
      return sections.filter((s) => s.id === 0);
    }
    return sections;
  };

  const visibleSections = getVisibleSections();

  const getItems = () => {
    if (!overview) {
      // Original logic for non-overview mode
      if (quiz) return quizItems;
      if (flashcards) return flashcardItems;
      if (textLabbet) return textLabbetItems;
      if (powerpoint) return powerpointItems;
      if (podcast) return podcastItems;

      if (grading) return gradingItems;
      if (assistent) return assistentItems;
      if (vision) return visionItems;
      if (lessonPlan) return lessonPlanItems;
      return lessonItems;
    }

    // For overview mode, return items based on selection
    const currentSection = visibleSections[selectedContentType];
    return currentSection ? currentSection.items : lessonItems;
  };

  const currentItems = getItems();

  return (
    <Paper
      sx={{
        borderRadius: { xs: "0px", md: "20px" },
        overflow: "hidden",
        maxWidth: "1400px",
        mx: "auto",
        mb: 4,
        boxShadow: { xs: "none", md: boxShadow },
      }}
    >
      <Box>
        {overview && (
          <Box sx={{ position: "relative" }}>
            <Box
              sx={{
                display: "flex",
                overflowX: "auto",
                overflowY: "hidden",

                background: backgroundGradient,
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: clipPathBackground,
                  clipPath: "polygon(100% 0, 100% 100%, 0 100%, 50% 0)",
                },
                flexWrap: "nowrap",
                width: "100%",
                scrollbarWidth: "thin",
                "&::-webkit-scrollbar": {
                  height: "6px",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "rgba(255,255,255,0.3)",
                  borderRadius: "3px",
                },
              }}
            >
              {isMobile && (
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    right: 0,
                    bottom: 0,
                    width: "60px",
                    background:
                      "linear-gradient(to right, transparent, rgba(255,255,255,0.5))",
                    pointerEvents: "none",
                    zIndex: 100,
                  }}
                />
              )}
              {visibleSections.map((section) => {
                const isActive =
                  selectedContentType ===
                  visibleSections.findIndex((s) => s.id === section.id);
                return (
                  <Box
                    key={section.id}
                    onClick={() => {
                      if (
                        selectedContentType ===
                        visibleSections.findIndex((s) => s.id === section.id)
                      ) {
                        // If already selected, apply animation via class
                        const el = document.getElementById(
                          `section-${section.id}`,
                        );
                        el.classList.add("pulse-animation");
                        setTimeout(
                          () => el.classList.remove("pulse-animation"),
                          300,
                        );
                      } else {
                        setSelectedContentType(
                          visibleSections.findIndex((s) => s.id === section.id),
                        );
                        setTabValue(0);
                      }
                    }}
                    id={`section-${section.id}`}
                    sx={{
                      "&.pulse-animation": {
                        animation: "pulse 0.3s ease",
                      },
                      "@keyframes pulse": {
                        "0%": { transform: "scale(1)" },
                        "50%": { transform: "scale(0.95)" },
                        "100%": { transform: "scale(1)" },
                      },
                      flex: "0 0 auto", // This is key - don't grow, don't shrink, use base size
                      minWidth: { xs: "110px", sm: "120px", md: "120px" },
                      flexBasis: { md: "0", lg: "0" },
                      flexGrow: { md: 1, lg: 1 },
                      position: "relative",
                      py: { xs: 2, md: insideApp ? 2 : 3 },
                      px: { xs: 3.5, md: insideApp ? 2.5 : 3.5 },
                      cursor: "pointer",
                      transition: "background-color 0.2s",

                      bgcolor: isActive
                        ? "rgb(240 248 255 / 14%)"
                        : "transparent",

                      "& .section-title": {
                        borderBottom: isActive ? "2px solid white" : "",
                        fontWeight: isActive ? 600 : 500,
                      },

                      "&:hover": {
                        bgcolor: isActive
                          ? "rgb(240 248 255 / 14%)"
                          : "rgba(255, 255, 255, 0.1)",
                        "& .section-title": {
                          borderBottom: "2px solid white",
                        },
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                      }}
                    >
                      {React.createElement(MuiIcons[section.icon], {
                        sx: {
                          color: "white",
                          fontSize: { xs: 27, sm: 28, md: insideApp ? 30 : 35 },
                          mb: 1,
                          mt: 1,
                        },
                      })}
                      <Typography
                        className="section-title"
                        sx={{
                          color: "white",
                          fontSize: {
                            xs: "0.9rem",
                            sm: "0.8rem",
                            md: insideApp ? "0.9rem" : "1rem",
                          },
                          fontWeight: isActive ? 700 : 600,
                          textAlign: "center",
                          paddingBottom: "4px",
                          borderBottom: "2px solid transparent",
                          transition: "border-color 0.2s",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {section.name}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}

        {/* Funktions-tabs */}
        <Box
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            background: theme.palette.mode === "dark" ? "#22303c" : "#f9f9fa",
          }}
        >
          <Box sx={{ position: "relative" }}>
            <Tabs
              value={tabValue}
              onChange={(e, newValue) => setTabValue(newValue)}
              variant="scrollable"
              scrollButtons="auto"
              TabIndicatorProps={{
                children: <span className="MuiTabs-indicatorSpan" />,
              }}
              sx={{
                minHeight: "60px",
                p: 1,
                pt: 2,
                pb: 2,
                "& .MuiTabs-flexContainer": {
                  justifyContent: "flex-start",
                },
                "& .MuiTabs-indicator": {
                  display: "flex",
                  justifyContent: "center",
                  backgroundColor: "transparent",
                  height: insideApp ? "0px" : "0px", // Set height for the container
                },
                "& .MuiTabs-indicatorSpan": {
                  maxWidth: "80%",
                  borderRadius: 2,
                  width: "100%",
                  backgroundColor:
                    theme.palette.themeType === "allakando"
                      ? "rgb(121, 162, 33)"
                      : "rgba(139, 92, 246, 1)",
                  height: "100%", // Make the span take full height of parent
                },
              }}
            >
              {currentItems.map((item, index) => (
                <Tab
                  key={index}
                  icon={
                    item.icon ? React.createElement(MuiIcons[item.icon]) : null
                  }
                  label={item.title}
                  iconPosition="start"
                  sx={{
                    minWidth: "100px",
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    fontWeight: "500",
                    p: 2,
                    minHeight: "40px !important", // Force minimum height
                    height: {
                      xs: "50px !important",
                      md: insideApp ? "40px !important" : "60px !important",
                    }, // Force exact height
                    maxHeight: "60px !important", // Force maximum height
                    padding: "0 16px !important", // Reset padding
                    paddingTop: "0 !important", // Force top padding to 0

                    borderRadius: 50,
                    marginRight: 1.5,
                    fontSize: insideApp
                      ? "0.9rem"
                      : { xs: "0.9rem", md: "1rem" },
                    backgroundColor: theme.palette.background.paper,

                    border:
                      theme.palette.mode === "dark"
                        ? "1px solid #3e4954"
                        : "1px solid #dbdbdc",

                    "& .MuiTab-iconWrapper": {
                      marginRight: 1,
                      marginBottom: 0,
                    },
                    "& .MuiSvgIcon-root": {
                      fontSize: insideApp ? "1.2rem" : "1.4rem",
                      marginRight: 1,
                      marginBottom: "0 !important",
                    },
                    "&:hover": {
                      backgroundColor:
                        theme.palette.themeType === "allakando"
                          ? "rgba(121, 162, 33, 0.08)"
                          : "rgba(139, 92, 246, 0.08)",
                      "& .MuiSvgIcon-root": {
                        color:
                          theme.palette.themeType === "allakando"
                            ? "rgb(121, 162, 33)"
                            : "rgba(139, 92, 246, 1)",
                      },
                    },
                    "&.Mui-selected": {
                      backgroundColor: "#8b5cf6",
                      border: "1px solid transparent",

                      color: "white",
                      fontWeight: 600,
                      //backgroundColor:"rgba(139, 92, 246, 0.12)",

                      "& .MuiSvgIcon-root": {
                        color:
                          theme.palette.themeType === "allakando"
                            ? "rgb(121, 162, 33)"
                            : "white",
                      },
                    },
                  }}
                />
              ))}
            </Tabs>

            {isMobile && (
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  right: 0,
                  bottom: 0,
                  width: "60px",
                  background:
                    "linear-gradient(to right, transparent, rgba(255,255,255,0.9))",
                  pointerEvents: "none",
                  zIndex: 9999,
                }}
              />
            )}
          </Box>
        </Box>

        {/* Innehållspanel */}
        <Box sx={{ px: 4, py: 5 }}>
          {currentItems.map((item, index) => (
            <div key={index} role="tabpanel" hidden={tabValue !== index}>
              {tabValue === index && (
                <Grid container spacing={4}>
                  <Grid item xs={12} md={6} sx={{ order: { xs: 2, md: 1 } }}>
                    <Box
                      sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "flex-start",
                      }}
                    >
                      <Typography
                        variant="h5"
                        component="h3"
                        sx={{
                          fontWeight: "700",
                          mb: { xs: 2, md: 5 },
                          fontSize: { xs: "1.5rem", md: " 2.1rem" },
                          display: { xs: "none", md: "block" },
                        }}
                      >
                        {item.title}
                      </Typography>
                      {item.listItems?.map((listItem, index) => (
                        <InstructionItem
                          key={index}
                          text={listItem.text}
                          marginBottom={1}
                          simple="true"
                          fontWeight="400"
                        />
                      ))}
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6} sx={{ order: { xs: 1, md: 2 } }}>
                    <Typography
                      variant="h5"
                      component="h3"
                      sx={{
                        fontWeight: "700",
                        mb: { xs: 2, md: 5 },
                        fontSize: { xs: "1.5rem", md: " 2.1rem" },
                        display: { xs: "block", md: "none" },
                      }}
                    >
                      {item.title}
                    </Typography>
                    <VideoPlayer
                      videoUrl={item.videoUrl}
                      poster={item.poster}
                    />
                  </Grid>
                </Grid>
              )}
            </div>
          ))}
        </Box>
      </Box>
    </Paper>
  );
};

export default HelpContent;
